/**
 * One error type for the whole API layer, and one axis to branch on.
 *
 * The server answers every refusal in the same shape — `{"error": {"code",
 * "message", "fields"?, "failure"?}}` — and the code is the stable part. Screens switch on
 * `code` and never on `message`: the sentence is written for a person, it is
 * the server's own English, and it is reworded whenever somebody improves it.
 *
 * The set below is the server's own list plus two codes no server sends,
 * because they describe something that happened instead of an answer.
 */

import type { IdentityFailure } from './types';

/** What the console concluded without an answer from the server. */
export type TransportErrorCode =
  /** The request never got through, or the answer was not readable. */
  | 'network'
  /** An answer arrived carrying a code this console does not know. */
  | 'unexpected';

/** Every refusal the server has a code for, in the order of docs/api.md §5. */
export type ServerErrorCode =
  | 'unauthenticated'
  | 'bad_credentials'
  | 'too_many_attempts'
  | 'forbidden'
  /** Nothing can sign in here just now: no provider applied, or one being changed. */
  | 'sign_in_unavailable'
  /** The return is being finished in another browser, or too late. One code on purpose. */
  | 'sign_in_expired'
  /** The identity did not verify. One code for every reason, also on purpose. */
  | 'sign_in_refused'
  /** The tenancy's own provider did not answer. Not about the person in front of it. */
  | 'provider_unreachable'
  | 'no_such_console'
  | 'tenant_not_found'
  | 'resource_not_found'
  | 'account_not_found'
  | 'invalid_request'
  | 'provider_identity_unavailable'
  /**
   * The identity provider a tenancy would be stood up on was asked, and it
   * cannot carry a cluster as given.
   *
   * The one code that carries a second one: `failure` says which of the checks
   * refused, and the six answers a provider can give need six different things
   * done about them. Read it with `failureKey` — the same function that names a
   * refused change, because it is the same vocabulary.
   */
  | 'identity_provider_refused'
  | 'conflict'
  | 'change_under_way'
  | 'no_failure_to_dismiss'
  | 'tenant_unreachable'
  /** The tenancy's own cluster refused, or could not be reached. Its words are
   * in `message`, and they are what a person acts on. */
  | 'cluster_refused'
  /**
   * The tenancy's own gdsgate does not know the event class its audit was asked
   * to be narrowed by, and refused the whole read over the word.
   *
   * A refinement of `cluster_refused` rather than a replacement for it. The
   * control plane tells the two apart by the argument parser's own wording, so
   * a wording it has not been taught leaves the refusal travelling as it always
   * did — under `cluster_refused`, indistinguishable from a tenancy whose
   * policy keeps this console out of its chain. Both paths stay handled.
   */
  | 'audit_class_unknown'
  /** The caller is who they say and may do it; their name is one gdsgate will not record. */
  | 'operator_not_nameable'
  | 'internal';

export type ApiErrorCode = TransportErrorCode | ServerErrorCode;

/**
 * The server's list, as the runtime sees it.
 *
 * Typed as the union rather than as strings, so a code misspelled here is a
 * build error rather than a refusal that quietly reads as `unexpected`.
 * Exported because a test that has to say "for every refusal this console can
 * draw" needs to enumerate them, and a second list written out by hand would be
 * the thing that goes stale.
 */
export const SERVER_CODES: readonly ServerErrorCode[] = [
  'unauthenticated',
  'bad_credentials',
  'too_many_attempts',
  'forbidden',
  'sign_in_unavailable',
  'sign_in_expired',
  'sign_in_refused',
  'provider_unreachable',
  'no_such_console',
  'tenant_not_found',
  'resource_not_found',
  'account_not_found',
  'invalid_request',
  'provider_identity_unavailable',
  'identity_provider_refused',
  'conflict',
  'change_under_way',
  'no_failure_to_dismiss',
  'tenant_unreachable',
  'cluster_refused',
  'audit_class_unknown',
  'operator_not_nameable',
  'internal',
];

/** Whether a string off the wire is a code this console knows how to answer. */
export function isServerErrorCode(value: string): value is ServerErrorCode {
  return (SERVER_CODES as readonly string[]).includes(value);
}

export interface ApiErrorDetails {
  /** The server's own sentence, when it sent one. */
  message?: string;
  /**
   * Which check of an identity provider refused, when that is what happened.
   *
   * Sent with `identity_provider_refused` and with nothing else. It is a code
   * rather than the sentence because the sentence is the server's English and
   * this console shows two languages.
   */
  failure?: IdentityFailure;
  /** Field-level complaints, keyed by the field name the request body uses. */
  fields?: Record<string, string>;
  /** The HTTP status, kept for diagnosis rather than for branching. */
  status?: number;
  /**
   * How long to wait, when the answer said so.
   *
   * Only `429` would carry it, and only if the deployment sends `Retry-After`.
   * Today's server sends no such header, so this is normally absent and the
   * screen says what happened rather than counting down — see
   * docs/api-expectations.md.
   */
  retryAfterSeconds?: number;
  /** The code the server sent, when it was not one this console knows. */
  unknownCode?: string;
}

export class ApiError extends Error {
  readonly code: ApiErrorCode;
  readonly failure: IdentityFailure | undefined;
  readonly fields: Readonly<Record<string, string>>;
  readonly status: number | undefined;
  readonly retryAfterSeconds: number | undefined;
  readonly unknownCode: string | undefined;

  constructor(code: ApiErrorCode, details: ApiErrorDetails = {}) {
    super(details.message ?? code);
    this.name = 'ApiError';
    this.code = code;
    this.failure = details.failure;
    this.fields = details.fields ?? {};
    this.status = details.status;
    this.retryAfterSeconds = details.retryAfterSeconds;
    this.unknownCode = details.unknownCode;
  }

  /** The complaint about one form field, if the refusal was about that field. */
  fieldError(name: string): string | undefined {
    return this.fields[name];
  }
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}

/** Anything thrown out of the API layer, normalised. */
export function toApiError(value: unknown): ApiError {
  return isApiError(value) ? value : new ApiError('unexpected', { message: String(value) });
}

/** `true` when a rejected promise carries exactly this refusal. */
export function hasCode(value: unknown, code: ApiErrorCode): boolean {
  return isApiError(value) && value.code === code;
}
